import { toast } from '@heroui/react';
import {
  handleError,
  type ErrorCode,
  type ErrorInfo,
  isValidationError,
} from '@hs/shared';

/**
 * ✅ 处理tRPC错误的便捷函数（带UI）
 * 
 * ⚠️ 重要：此函数只处理验证错误（UNPROCESSABLE_CONTENT）
 * 其他错误（网络错误、服务器错误等）由全局错误处理统一处理
 * 
 * 使用场景：
 * - 验证错误：需要显示在表单字段上或显示详细验证信息
 * - 业务模块不应该在 mutation 的 onError 中处理通用错误
 * 
 * 示例：
 * ```typescript
 * const mutation = trpc.users.create.useMutation({
 *   onError: (error) => {
 *     // 只处理验证错误，其他错误由全局处理
 *     handleTRPCError(error);
 *   },
 * });
 * ```
 */
export function handleTRPCError(
  error: unknown,
  options?: {
    /** 自定义错误消息映射 */
    customMessages?: Partial<Record<ErrorCode, string>>;
    /** 错误回调 */
    onError?: (errorInfo: ErrorInfo) => void;
  }
): void {
  const errorInfo = handleError(error, {
    customMessages: options?.customMessages,
  });

  // ✅ 只处理验证错误，其他错误由全局错误处理统一处理
  if (errorInfo.code === 'UNPROCESSABLE_CONTENT' && errorInfo.zodError) {
    const validationMessages = errorInfo.zodError.issues
      .map((issue) => {
        const path = issue.path.length > 0 ? issue.path.join('.') : '参数';
        return `${path}: ${issue.message}`;
      })
      .join('\n');

    toast.danger('请求参数错误', {
      description: validationMessages,
      timeout: 5000,
    });
  }
  // 其他错误（网络错误、服务器错误等）由全局错误处理统一处理，不在这里处理

  // 调用回调
  options?.onError?.(errorInfo);

  // 开发环境：输出详细信息
  if (import.meta.env.DEV) {
    console.group(`❌ Error [${errorInfo.code}]`);
    console.error('Message:', errorInfo.message);
    console.error('HTTP Status:', errorInfo.httpStatus);
    if (errorInfo.zodError) {
      console.error('Validation Issues:', errorInfo.zodError.issues);
    }
    if (errorInfo.details) {
      console.error('Details:', errorInfo.details);
    }
    console.error('Original:', errorInfo.originalError);
    console.groupEnd();
  }
}

/**
 * ✅ 显示简单错误消息
 */
export function showErrorMessage(
  error: unknown,
  options?: {
    customMessages?: Partial<Record<ErrorCode, string>>;
  }
): void {
  const errorInfo = handleError(error, options);
  toast.danger('错误', {
    description: errorInfo.message,
    timeout: 3000,
  });
}

/**
 * ✅ 显示详细错误通知
 */
export function showErrorNotification(
  error: unknown,
  options?: {
    customMessages?: Partial<Record<ErrorCode, string>>;
    title?: string;
    duration?: number;
  }
): void {
  const errorInfo = handleError(error, { customMessages: options?.customMessages });

  let description = errorInfo.message;
  
  // 如果是验证错误，显示所有验证问题
  if (errorInfo.code === 'UNPROCESSABLE_CONTENT' && errorInfo.zodError) {
    description = errorInfo.zodError.issues
      .map((issue) => {
        const path = issue.path.length > 0 ? issue.path.join('.') : '参数';
        return `${path}: ${issue.message}`;
      })
      .join('\n');
  }

  toast.danger(options?.title || '操作失败', {
    description,
    timeout: options?.duration ?? 5000,
  });
}

// ✅ 重新导出共享工具
export {
  handleError,
  isValidationError,
  type ErrorCode,
  type ErrorInfo,
} from '@hs/shared';

